import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Film, Phone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAIVideoStore } from '@/hooks/use-ai-video-store';
import AIVideoHeader from '../AIVideoHeader';
import AIVideoFooter from '../AIVideoFooter';
import VideoBackground from '../VideoBackground';

interface Step9EmailProps {
  onNext: () => void;
  onBack: () => void;
  answers: any;
}

const Step9Email: React.FC<Step9EmailProps> = ({ onNext, onBack, answers }) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();
  const { setAnswer } = useAIVideoStore();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    setError('');
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    setIsLoading(true);

    try {
      // Save to AI Video store
      setAnswer('email', email.trim());
      setAnswer('phone', phone.trim());

      // Save lead to database
      const { error: insertError } = await supabase
        .from('leads')
        .insert({
          email: email.trim(),
          phone: phone.trim(),
          onboarding_answers: {
            ...answers,
            funnel_type: 'ai-video-course'
          },
          step_reached: 9
        });

      if (insertError) {
        console.error('Error saving lead:', insertError);
        toast({
          title: "Error",
          description: "Failed to save your information. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success!",
        description: "Your information has been saved. Redirecting to payment...",
      });

      setTimeout(() => {
        onNext();
      }, 1000);

    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fallbackBackground = (
    <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
      <motion.div
        className="absolute inset-0 opacity-20"
        animate={{
          background: [
            "radial-gradient(circle at 20% 50%, #ffffff 1px, transparent 1px)",
            "radial-gradient(circle at 80% 50%, #ffffff 1px, transparent 1px)",
            "radial-gradient(circle at 40% 50%, #ffffff 1px, transparent 1px)"
          ]
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      <VideoBackground
        videoSrc="https://res.cloudinary.com/drinkamrs/video/upload/v1752753031/Characters_wole_ai_02_a_202507170450_g9mto0.mp4"
        fallbackComponent={fallbackBackground}
      />
      <AIVideoHeader 
        currentStep={9} 
        totalSteps={8} 
        onBack={onBack} 
      />

      <div className="relative z-10 flex-1 px-4 py-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-sm mx-auto text-center w-full"
        >
          {/* Icon */}
          <motion.div 
            className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-hollywood-gold to-hollywood-gold-light rounded-full flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Film className="w-8 h-8 text-white" />
          </motion.div>

          {/* Headline */}
          <motion.h2 
            className="text-xl font-bold text-white mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Unlock Your{' '}
            <span className="bg-gradient-to-r from-hollywood-gold to-hollywood-shimmer bg-clip-text text-transparent font-extrabold">
              AI Video Creation Blueprint!
            </span>
          </motion.h2>

          {/* Sub-headline */}
          <motion.p 
            className="text-sm text-white/80 mb-6 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Enter your details to receive your tailored guide for generating stunning videos with AI.
          </motion.p>

          {/* Email Input */}
          <motion.div
            className="mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-hollywood-gold w-4 h-4" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Type your email"
                className="w-full pl-10 pr-4 py-3 border border-hollywood-gold/30 bg-black/30 backdrop-blur-sm rounded-lg focus:ring-2 focus:ring-hollywood-gold focus:border-hollywood-gold-light text-sm text-white placeholder-white/60"
                disabled={isLoading}
              />
            </div>
          </motion.div>

          {/* Phone Input */}
          <motion.div
            className="mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-hollywood-gold w-4 h-4" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Type your phone number"
                className="w-full pl-10 pr-4 py-3 border border-hollywood-gold/30 bg-black/30 backdrop-blur-sm rounded-lg focus:ring-2 focus:ring-hollywood-gold focus:border-hollywood-gold-light text-sm text-white placeholder-white/60"
                disabled={isLoading}
              />
            </div>
            {error && (
              <motion.p 
                className="text-red-400 text-xs mt-1 text-left"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {error}
              </motion.p>
            )}
          </motion.div>

          {/* Disclaimer */}
          <motion.p 
            className="text-xs text-white/60 mb-4 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            By clicking continue, you agree to iSelect Digital's{' '}
            <a href="#" className="text-hollywood-gold hover:underline">Terms & Conditions</a>{' '}
            and{' '}
            <a href="#" className="text-hollywood-gold hover:underline">Privacy Policy</a>.
          </motion.p>

          {/* CTA Button */}
          <motion.button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-hollywood-gold-dark via-hollywood-gold to-hollywood-gold-light text-white py-3 rounded-lg font-semibold text-sm shadow-lg hover:shadow-hollywood-shimmer/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            whileHover={{ scale: isLoading ? 1 : 1.02 }}
            whileTap={{ scale: isLoading ? 1 : 0.98 }}
          >
            {isLoading ? 'Processing...' : 'Continue'}
          </motion.button>
        </motion.div>
      </div>

      <AIVideoFooter />
    </div>
  );
};

export default Step9Email;